/*
 * @Author: chengyu
 * @Date: 2022-09-24 01:51:51
 * @Description: 缓存插件
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { getLbsConfig } from '../../config';
import { AmapStandardEnum } from '../../enums/lbs';
import { MapApiEnum, MapRgcStatusEnum } from '../../enums/ubt';
import { getCache, getSignature, setCache } from '../../utils/cache';
import { createCacheGeoHash } from '../../utils/cacheGeoHash';
import { getCachePromise, setCachePromise } from '../../utils/cachePromise';
import { cacheEmit } from '../../utils/emitter';
import { createSilentError } from '../../utils/error';
import { isArray, isEmptyArray, isNetworkError, isObject, } from '../../utils/is';
import { mapError } from '../../utils/logger';
import { ubtMapApi, ubtRgcStatistics } from '../../utils/ubt';
/**
 * - 只有单经纬度支持`逆地理编码`缓存
 * - `locations`为多经纬度入参的话，逆地理缓存将不受支持
 * @param params
 */
function isSupportRgcCache(params) {
    var query = params.query;
    if (isObject(query)) {
        var locations = query.locations;
        return isArray(locations) && locations.length === 1;
    }
    return false;
}
/**
 * 获取一个正确格式的经纬度
 *
 * ```ts
 * tryPoint({
 *  query: {
 *  locations: [{ lat: 30.287307, lng: 120.11747 }]
 *  }
 * });
 * // success return { lat: 30.287307, lng: 120.11747 }
 *
 * tryPoint({
 *  query: {
 *  locations: [{ lat: 30.287307, lng: 120.11747 }]
 *  }
 * });
 * // fail undefined
 * ```
 * @param params
 */
function tryPoint(params) {
    var query = params.query;
    if (isEmptyArray(query.locations))
        return undefined;
    var point = query.locations[0];
    if (isObject(point) && point.lng && point.lat)
        return point;
    return undefined;
}
/**
 * 计算 cacheKey
 *
 * ```
 * 高德
 * => GAODE_xxx
 *
 * 哈罗lbs
 * => HELLO_xxx
 * ```
 */
function getCacheKey(_a) {
    var params = _a.params, cache = _a.cache, serviceType = _a.serviceType, method = _a.method;
    try {
        if (!cache || !params)
            return;
        var queryKey = cache.httpCache || cache.resultCache ? getSignature(params) : undefined;
        // 区分来源 高德 or 哈罗lbs
        if (queryKey)
            return "".concat(method, "_").concat(serviceType, "_").concat(queryKey);
    }
    catch (e) {
        mapError('getCacheKey', e);
    }
    return;
}
/**
 * 计算逆地理缓存`tag`，限制缓存命中范围
 *
 * - 除了`经纬度`以外，不同的入参不能命中同一缓存
 * - 通过`逆地理编码入参`生成md5签名`tag`,作为缓存的`tag`，参数一致才能命中
 */
function createRgcTag(_a) {
    var serviceType = _a.serviceType, query = _a.query;
    if (isObject(query)) {
        var signature = getSignature({
            poiType: query.poiType,
            radius: query.radius,
            extensions: query.extensions,
            roadlevel: query.roadlevel,
            maxSize: query.maxSize,
            // 默认是 0
            isAmapStandard: query.isAmapStandard || AmapStandardEnum.HELLO,
        });
        // serviceType 作为缓存的tag进行存储，用来区分，不同的数据来源
        if (signature)
            return "".concat(serviceType, ":").concat(signature);
    }
    return undefined;
}
function cachePlugin(options) {
    var cache = options.cache, params = options.params, serviceType = options.serviceType, context = options.context, fallbackRegeo = options.fallbackRegeo, mapOptions = options.mapOptions, method = options.method;
    if (!cache)
        return {};
    if (!isObject(cache))
        return {};
    var geoHashCache = cache.geoHashCache;
    var cacheKey = getCacheKey(options);
    var appKey = (mapOptions || {}).appKey;
    var lbsAppKey = getLbsConfig().appKey;
    var _appKey = appKey || lbsAppKey;
    var startTime = new Date().getTime();
    return {
        onBefore: function () {
            var _a;
            // 逆地理缓存，只有逆地理编码接口才能开启
            if (geoHashCache &&
                method === 'reGeoCodingSearch' &&
                isSupportRgcCache(params)) {
                // 计算缓存`tag`签名
                var tag = createRgcTag({ serviceType: serviceType, query: params.query });
                if (!tag)
                    return {};
                var cache_1 = createCacheGeoHash({ tag: tag });
                // 如果为空就直接返回呗
                if (!cache_1)
                    return {};
                var point = tryPoint(params);
                if (point) {
                    // 尝试命中 高精度缓存
                    var response = cache_1 === null || cache_1 === void 0 ? void 0 : cache_1.get(point);
                    var ubtRgcParams = {
                        gaodeStartTime: context === null || context === void 0 ? void 0 : context.startTime,
                        // 缓存只在高德请求开始前尝试命中，故没有`lbsStartTime`开始时间
                        // lbsStartTime: start,
                        type: options.serviceType,
                        params: params,
                        appKey: _appKey,
                        response: response,
                        startTime: context === null || context === void 0 ? void 0 : context.startTime,
                    };
                    if (isObject(response)) {
                        ubtRgcStatistics(__assign(__assign({}, ubtRgcParams), { status: MapRgcStatusEnum.high }));
                        return { response: response };
                    }
                    // 超限报错 尝试命中 粗缓存
                    if ((_a = context === null || context === void 0 ? void 0 : context.rgc) === null || _a === void 0 ? void 0 : _a.overError) {
                        var responseCrude = cache_1 === null || cache_1 === void 0 ? void 0 : cache_1.crude(point);
                        // 命中粗缓存
                        if (isObject(responseCrude)) {
                            ubtRgcStatistics(__assign(__assign({}, ubtRgcParams), { status: MapRgcStatusEnum.low }));
                            return {
                                response: responseCrude,
                            };
                        }
                        ubtRgcStatistics(__assign(__assign({}, ubtRgcParams), { status: MapRgcStatusEnum.unknown }));
                        // 未命中则抛出异常
                        throw createSilentError({ message: '未命中->粗精度' });
                    }
                }
            }
            else if (cache.resultCache) {
                var response = getCache(cacheKey);
                if (response) {
                    cacheEmit('resultCache', cacheKey, response, {
                        fetchOptions: options,
                    });
                    ubtMapApi({
                        fetchOptions: options,
                        context: context,
                        params: params,
                        response: response,
                        startTime: startTime,
                        error: null,
                        status: MapApiEnum.resultCache,
                    });
                    return { response: response };
                }
            }
            return {};
        },
        onRequest: function (server, params) {
            // 未开启 http 请求缓存，直接`return`
            if (!cache.httpCache)
                return {};
            if (!cacheKey)
                return {};
            var cachePromise = getCachePromise(cacheKey);
            if (cachePromise) {
                cacheEmit('httpCache', cacheKey, undefined, {
                    fetchOptions: options,
                });
                ubtMapApi({
                    fetchOptions: options,
                    context: context,
                    params: params,
                    response: {},
                    startTime: startTime,
                    error: null,
                    status: MapApiEnum.httpCache,
                });
                return { servicePromise: cachePromise };
            }
            var servicePromise = server(params, context);
            setCachePromise(cacheKey, servicePromise);
            return { servicePromise: servicePromise };
        },
        onSuccess: function (data) {
            if (geoHashCache && isSupportRgcCache(params)) {
                var point = tryPoint(params);
                if (point) {
                    var tag = createRgcTag({ serviceType: serviceType, query: params.query });
                    if (tag) {
                        var cache_2 = createCacheGeoHash({ tag: tag });
                        cache_2 === null || cache_2 === void 0 ? void 0 : cache_2.set(point, data);
                    }
                }
            }
            else if (cache.resultCache) {
                setCache(cacheKey, data);
            }
        },
        onError: function (e) {
            var _a, _b, _c;
            // 拥塞处理，逆地理编码超限标记处理
            if (fallbackRegeo && isNetworkError(e)) {
                var infoCode = (_b = (_a = e.fetchData) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.infoCode;
                if (infoCode) {
                    (_c = context === null || context === void 0 ? void 0 : context.rgc) === null || _c === void 0 ? void 0 : _c.setOverError(+infoCode);
                }
            }
        },
    };
}
export default cachePlugin;
//# sourceMappingURL=data:application/json;base64,eyJ2ZXJzaW9uIjozLCJmaWxlIjoiY2FjaGVQbHVnaW4uanMiLCJzb3VyY2VSb290IjoiIiwic291cmNlcyI6WyIuLi8uLi8uLi8uLi9zcmMvY29yZS9yZXF1ZXN0L3BsdWdpbnMvY2FjaGVQbHVnaW4udHMiXSwibmFtZXMiOltdLCJtYXBwaW5ncyI6IkFBQUE7Ozs7R0FJRzs7Ozs7Ozs7Ozs7O0FBU0gsT0FBTyxFQUFFLFlBQVksRUFBRSxNQUFNLGNBQWMsQ0FBQztBQUU1QyxPQUFPLEVBQUUsZ0JBQWdCLEVBQUUsTUFBTSxpQkFBaUIsQ0FBQztBQUNuRCxPQUFPLEVBQUUsVUFBVSxFQUFFLGdCQUFnQixFQUFFLE1BQU0saUJBQWlCLENBQUM7QUFFL0QsT0FBTyxFQUFFLFFBQVEsRUFBRSxZQUFZLEVBQUUsUUFBUSxFQUFFLE1BQU0sbUJBQW1CLENBQUM7QUFDckUsT0FBTyxFQUFFLGtCQUFrQixFQUFFLE1BQU0sMEJBQTBCLENBQUM7QUFDOUQsT0FBTyxFQUFFLGVBQWUsRUFBRSxlQUFlLEVBQUUsTUFBTSwwQkFBMEIsQ0FBQztBQUM1RSxPQUFPLEVBQUUsU0FBUyxFQUFFLE1BQU0scUJBQXFCLENBQUM7QUFDaEQsT0FBTyxFQUFFLGlCQUFpQixFQUFFLE1BQU0sbUJBQW1CLENBQUM7QUFDdEQsT0FBTyxFQUNMLE9BQU8sRUFDUCxZQUFZLEVBQ1osY0FBYyxFQUNkLFFBQVEsR0FDVCxNQUFNLGdCQUFnQixDQUFDO0FBQ3hCLE9BQU8sRUFBRSxRQUFRLEVBQUUsTUFBTSxvQkFBb0IsQ0FBQztBQUM5QyxPQUFPLEVBQUUsU0FBUyxFQUFFLGdCQUFnQixFQUFFLE1BQU0saUJBQWlCLENBQUM7QUFFOUQ7Ozs7R0FJRztBQUNILFNBQVMsaUJBQWlCLENBQUMsTUFBbUI7SUFDNUMsSUFBTSxLQUFLLEdBQUcsTUFBTSxDQUFDLEtBQWdDLENBQUM7SUFFdEQsSUFBSSxRQUFRLENBQUMsS0FBSyxDQUFDLEVBQUU7UUFDbkIsSUFBTSxTQUFTLEdBQUcsS0FBSyxDQUFDLFNBQVMsQ0FBQztRQUNsQyxPQUFPLE9BQU8sQ0FBQyxTQUFTLENBQUMsSUFBSSxTQUFTLENBQUMsTUFBTSxLQUFLLENBQUMsQ0FBQztLQUNyRDtJQUVELE9BQU8sS0FBSyxDQUFDO0FBQ2YsQ0FBQztBQUVEOzs7Ozs7Ozs7Ozs7Ozs7Ozs7O0dBbUJHO0FBQ0gsU0FBUyxRQUFRLENBQUMsTUFBbUI7SUFDbkMsSUFBTSxLQUFLLEdBQUcsTUFBTSxDQUFDLEtBQWdDLENBQUM7SUFDdEQsSUFBSSxZQUFZLENBQUMsS0FBSyxDQUFDLFNBQVMsQ0FBQztRQUFFLE9BQU8sU0FBUyxDQUFDO0lBQ3BELElBQU0sS0FBSyxHQUFHLEtBQUssQ0FBQyxTQUFTLENBQUMsQ0FBQyxDQUFDLENBQUM7SUFDakMsSUFBSSxRQUFRLENBQUMsS0FBSyxDQUFDLElBQUksS0FBSyxDQUFDLEdBQUcsSUFBSSxLQUFLLENBQUMsR0FBRztRQUFFLE9BQU8sS0FBSyxDQUFDO0lBQzVELE9BQU8sU0FBUyxDQUFDO0FBQ25CLENBQUM7QUFFRDs7Ozs7Ozs7OztHQVVHO0FBQ0gsU0FBUyxXQUFXLENBQUMsRUFBb0Q7UUFBbEQsTUFBTSxZQUFBLEVBQUUsS0FBSyxXQUFBLEVBQUUsV0FBVyxpQkFBQSxFQUFFLE1BQU0sWUFBQTtJQUN2RCxJQUFJO1FBQ0YsSUFBSSxDQUFDLEtBQUssSUFBSSxDQUFDLE1BQU07WUFBRSxPQUFPO1FBRTlCLElBQU0sUUFBUSxHQUNaLEtBQUssQ0FBQyxTQUFTLElBQUksS0FBSyxDQUFDLFdBQVcsQ0FBQyxDQUFDLENBQUMsWUFBWSxDQUFDLE1BQU0sQ0FBQyxDQUFDLENBQUMsQ0FBQyxTQUFTLENBQUM7UUFFMUUsbUJBQW1CO1FBQ25CLElBQUksUUFBUTtZQUFFLE9BQU8sVUFBRyxNQUFNLGNBQUksV0FBVyxjQUFJLFFBQVEsQ0FBRSxDQUFDO0tBQzdEO0lBQUMsT0FBTyxDQUFDLEVBQUU7UUFDVixRQUFRLENBQUMsYUFBYSxFQUFFLENBQUMsQ0FBQyxDQUFDO0tBQzVCO0lBQ0QsT0FBTztBQUNULENBQUM7QUFPRDs7Ozs7R0FLRztBQUNILFNBQVMsWUFBWSxDQUFDLEVBQXFDO1FBQW5DLFdBQVcsaUJBQUEsRUFBRSxLQUFLLFdBQUE7SUFDeEMsSUFBSSxRQUFRLENBQUMsS0FBSyxDQUFDLEVBQUU7UUFDbkIsSUFBTSxTQUFTLEdBQUcsWUFBWSxDQUFDO1lBQzdCLE9BQU8sRUFBRSxLQUFLLENBQUMsT0FBTztZQUN0QixNQUFNLEVBQUUsS0FBSyxDQUFDLE1BQU07WUFDcEIsVUFBVSxFQUFFLEtBQUssQ0FBQyxVQUFVO1lBQzVCLFNBQVMsRUFBRSxLQUFLLENBQUMsU0FBUztZQUMxQixPQUFPLEVBQUUsS0FBSyxDQUFDLE9BQU87WUFDdEIsUUFBUTtZQUNSLGNBQWMsRUFBRSxLQUFLLENBQUMsY0FBYyxJQUFJLGdCQUFnQixDQUFDLEtBQUs7U0FDL0QsQ0FBQyxDQUFDO1FBRUgsd0NBQXdDO1FBQ3hDLElBQUksU0FBUztZQUFFLE9BQU8sVUFBRyxXQUFXLGNBQUksU0FBUyxDQUFFLENBQUM7S0FDckQ7SUFFRCxPQUFPLFNBQVMsQ0FBQztBQUNuQixDQUFDO0FBRUQsU0FBUyxXQUFXLENBQUksT0FBcUI7SUFFekMsSUFBQSxLQUFLLEdBT0gsT0FBTyxNQVBKLEVBQ0wsTUFBTSxHQU1KLE9BQU8sT0FOSCxFQUNOLFdBQVcsR0FLVCxPQUFPLFlBTEUsRUFDWCxPQUFPLEdBSUwsT0FBTyxRQUpGLEVBQ1AsYUFBYSxHQUdYLE9BQU8sY0FISSxFQUNiLFVBQVUsR0FFUixPQUFPLFdBRkMsRUFDVixNQUFNLEdBQ0osT0FBTyxPQURILENBQ0k7SUFFWixJQUFJLENBQUMsS0FBSztRQUFFLE9BQU8sRUFBRSxDQUFDO0lBQ3RCLElBQUksQ0FBQyxRQUFRLENBQUMsS0FBSyxDQUFDO1FBQUUsT0FBTyxFQUFFLENBQUM7SUFFeEIsSUFBQSxZQUFZLEdBQUssS0FBSyxhQUFWLENBQVc7SUFDL0IsSUFBTSxRQUFRLEdBQUcsV0FBVyxDQUFDLE9BQU8sQ0FBQyxDQUFDO0lBQzlCLElBQUEsTUFBTSxHQUFLLENBQUEsVUFBVSxJQUFJLEVBQUUsQ0FBQSxPQUFyQixDQUFzQjtJQUM1QixJQUFRLFNBQVMsR0FBSyxZQUFZLEVBQUUsT0FBbkIsQ0FBb0I7SUFDN0MsSUFBTSxPQUFPLEdBQUcsTUFBTSxJQUFJLFNBQVMsQ0FBQztJQUNwQyxJQUFNLFNBQVMsR0FBRyxJQUFJLElBQUksRUFBRSxDQUFDLE9BQU8sRUFBRSxDQUFDO0lBRXZDLE9BQU87UUFDTCxRQUFRLEVBQUU7O1lBQ1Isc0JBQXNCO1lBQ3RCLElBQ0UsWUFBWTtnQkFDWixNQUFNLEtBQUssbUJBQW1CO2dCQUM5QixpQkFBaUIsQ0FBQyxNQUFNLENBQUMsRUFDekI7Z0JBQ0EsY0FBYztnQkFDZCxJQUFNLEdBQUcsR0FBRyxZQUFZLENBQUMsRUFBRSxXQUFXLGFBQUEsRUFBRSxLQUFLLEVBQUUsTUFBTSxDQUFDLEtBQUssRUFBRSxDQUFDLENBQUM7Z0JBQy9ELElBQUksQ0FBQyxHQUFHO29CQUFFLE9BQU8sRUFBRSxDQUFDO2dCQUNwQixJQUFNLE9BQUssR0FBRyxrQkFBa0IsQ0FBQyxFQUFFLEdBQUcsS0FBQSxFQUFFLENBQUMsQ0FBQztnQkFFMUMsYUFBYTtnQkFDYixJQUFJLENBQUMsT0FBSztvQkFBRSxPQUFPLEVBQUUsQ0FBQztnQkFFdEIsSUFBTSxLQUFLLEdBQUcsUUFBUSxDQUFDLE1BQU0sQ0FBQyxDQUFDO2dCQUUvQixJQUFJLEtBQUssRUFBRTtvQkFDVCxhQUFhO29CQUNiLElBQU0sUUFBUSxHQUFHLE9BQUssYUFBTCxPQUFLLHVCQUFMLE9BQUssQ0FBRSxHQUFHLENBQUMsS0FBSyxDQUFDLENBQUM7b0JBRW5DLElBQU0sWUFBWSxHQUFHO3dCQUNuQixjQUFjLEVBQUUsT0FBTyxhQUFQLE9BQU8sdUJBQVAsT0FBTyxDQUFFLFNBQVM7d0JBQ2xDLHdDQUF3Qzt3QkFDeEMsdUJBQXVCO3dCQUN2QixJQUFJLEVBQUUsT0FBTyxDQUFDLFdBQVc7d0JBQ3pCLE1BQU0sUUFBQTt3QkFDTixNQUFNLEVBQUUsT0FBTzt3QkFDZixRQUFRLFVBQUE7d0JBQ1IsU0FBUyxFQUFFLE9BQU8sYUFBUCxPQUFPLHVCQUFQLE9BQU8sQ0FBRSxTQUFTO3FCQUM5QixDQUFDO29CQUVGLElBQUksUUFBUSxDQUFDLFFBQVEsQ0FBQyxFQUFFO3dCQUN0QixnQkFBZ0IsdUJBQ1gsWUFBWSxLQUNmLE1BQU0sRUFBRSxnQkFBZ0IsQ0FBQyxJQUFJLElBQzdCLENBQUM7d0JBQ0gsT0FBTyxFQUFFLFFBQVEsVUFBQSxFQUFFLENBQUM7cUJBQ3JCO29CQUVELGdCQUFnQjtvQkFDaEIsSUFBSSxNQUFBLE9BQU8sYUFBUCxPQUFPLHVCQUFQLE9BQU8sQ0FBRSxHQUFHLDBDQUFFLFNBQVMsRUFBRTt3QkFDM0IsSUFBTSxhQUFhLEdBQUcsT0FBSyxhQUFMLE9BQUssdUJBQUwsT0FBSyxDQUFFLEtBQUssQ0FBQyxLQUFLLENBQUMsQ0FBQzt3QkFFMUMsUUFBUTt3QkFDUixJQUFJLFFBQVEsQ0FBQyxhQUFhLENBQUMsRUFBRTs0QkFDM0IsZ0JBQWdCLHVCQUNYLFlBQVksS0FDZixNQUFNLEVBQUUsZ0JBQWdCLENBQUMsR0FBRyxJQUM1QixDQUFDOzRCQUVILE9BQU87Z0NBQ0wsUUFBUSxFQUFFLGFBQWE7NkJBQ3hCLENBQUM7eUJBQ0g7d0JBRUQsZ0JBQWdCLHVCQUNYLFlBQVksS0FDZixNQUFNLEVBQUUsZ0JBQWdCLENBQUMsT0FBTyxJQUNoQyxDQUFDO3dCQUVILFdBQVc7d0JBQ1gsTUFBTSxpQkFBaUIsQ0FBQyxFQUFFLE9BQU8sRUFBRSxVQUFVLEVBQUUsQ0FBQyxDQUFDO3FCQUNsRDtpQkFDRjthQUNGO2lCQUFNLElBQUksS0FBSyxDQUFDLFdBQVcsRUFBRTtnQkFDNUIsSUFBTSxRQUFRLEdBQUcsUUFBUSxDQUFDLFFBQVEsQ0FBQyxDQUFDO2dCQUNwQyxJQUFJLFFBQVEsRUFBRTtvQkFDWixTQUFTLENBQUMsYUFBYSxFQUFFLFFBQVEsRUFBRSxRQUFRLEVBQUU7d0JBQzNDLFlBQVksRUFBRSxPQUFPO3FCQUN0QixDQUFDLENBQUM7b0JBQ0gsU0FBUyxDQUFDO3dCQUNSLFlBQVksRUFBRSxPQUFPO3dCQUNyQixPQUFPLFNBQUE7d0JBQ1AsTUFBTSxRQUFBO3dCQUNOLFFBQVEsVUFBQTt3QkFDUixTQUFTLFdBQUE7d0JBQ1QsS0FBSyxFQUFFLElBQUk7d0JBQ1gsTUFBTSxFQUFFLFVBQVUsQ0FBQyxXQUFXO3FCQUMvQixDQUFDLENBQUM7b0JBQ0gsT0FBTyxFQUFFLFFBQVEsVUFBQSxFQUFFLENBQUM7aUJBQ3JCO2FBQ0Y7WUFDRCxPQUFPLEVBQUUsQ0FBQztRQUNaLENBQUM7UUFDRCxTQUFTLEVBQUUsVUFBQyxNQUFNLEVBQUUsTUFBTTtZQUN4QiwyQkFBMkI7WUFDM0IsSUFBSSxDQUFDLEtBQUssQ0FBQyxTQUFTO2dCQUFFLE9BQU8sRUFBRSxDQUFDO1lBQ2hDLElBQUksQ0FBQyxRQUFRO2dCQUFFLE9BQU8sRUFBRSxDQUFDO1lBRXpCLElBQU0sWUFBWSxHQUFHLGVBQWUsQ0FBQyxRQUFRLENBQUMsQ0FBQztZQUUvQyxJQUFJLFlBQVksRUFBRTtnQkFDaEIsU0FBUyxDQUFDLFdBQVcsRUFBRSxRQUFRLEVBQUUsU0FBUyxFQUFFO29CQUMxQyxZQUFZLEVBQUUsT0FBTztpQkFDdEIsQ0FBQyxDQUFDO2dCQUNILFNBQVMsQ0FBQztvQkFDUixZQUFZLEVBQUUsT0FBTztvQkFDckIsT0FBTyxTQUFBO29CQUNQLE1BQU0sUUFBQTtvQkFDTixRQUFRLEVBQUUsRUFBRTtvQkFDWixTQUFTLFdBQUE7b0JBQ1QsS0FBSyxFQUFFLElBQUk7b0JBQ1gsTUFBTSxFQUFFLFVBQVUsQ0FBQyxTQUFTO2lCQUM3QixDQUFDLENBQUM7Z0JBQ0gsT0FBTyxFQUFFLGNBQWMsRUFBRSxZQUFZLEVBQUUsQ0FBQzthQUN6QztZQUVELElBQU0sY0FBYyxHQUFHLE1BQU0sQ0FBQyxNQUFNLEVBQUUsT0FBTyxDQUFDLENBQUM7WUFDL0MsZUFBZSxDQUFDLFFBQVEsRUFBRSxjQUFjLENBQUMsQ0FBQztZQUUxQyxPQUFPLEVBQUUsY0FBYyxnQkFBQSxFQUFFLENBQUM7UUFDNUIsQ0FBQztRQUNELFNBQVMsRUFBRSxVQUFDLElBQUk7WUFDZCxJQUFJLFlBQVksSUFBSSxpQkFBaUIsQ0FBQyxNQUFNLENBQUMsRUFBRTtnQkFDN0MsSUFBTSxLQUFLLEdBQUcsUUFBUSxDQUFDLE1BQU0sQ0FBQyxDQUFDO2dCQUUvQixJQUFJLEtBQUssRUFBRTtvQkFDVCxJQUFNLEdBQUcsR0FBRyxZQUFZLENBQUMsRUFBRSxXQUFXLGFBQUEsRUFBRSxLQUFLLEVBQUUsTUFBTSxDQUFDLEtBQUssRUFBRSxDQUFDLENBQUM7b0JBRS9ELElBQUksR0FBRyxFQUFFO3dCQUNQLElBQU0sT0FBSyxHQUFHLGtCQUFrQixDQUFDLEVBQUUsR0FBRyxLQUFBLEVBQUUsQ0FBQyxDQUFDO3dCQUMxQyxPQUFLLGFBQUwsT0FBSyx1QkFBTCxPQUFLLENBQUUsR0FBRyxDQUFDLEtBQUssRUFBRSxJQUFJLENBQUMsQ0FBQztxQkFDekI7aUJBQ0Y7YUFDRjtpQkFBTSxJQUFJLEtBQUssQ0FBQyxXQUFXLEVBQUU7Z0JBQzVCLFFBQVEsQ0FBQyxRQUFRLEVBQUUsSUFBSSxDQUFDLENBQUM7YUFDMUI7UUFDSCxDQUFDO1FBQ0QsT0FBTyxFQUFFLFVBQUMsQ0FBQzs7WUFDVCxtQkFBbUI7WUFDbkIsSUFBSSxhQUFhLElBQUksY0FBYyxDQUFDLENBQUMsQ0FBQyxFQUFFO2dCQUN0QyxJQUFNLFFBQVEsR0FBRyxNQUFBLE1BQUEsQ0FBQyxDQUFDLFNBQVMsMENBQUUsSUFBSSwwQ0FBRSxRQUFRLENBQUM7Z0JBRTdDLElBQUksUUFBUSxFQUFFO29CQUNaLE1BQUEsT0FBTyxhQUFQLE9BQU8sdUJBQVAsT0FBTyxDQUFFLEdBQUcsMENBQUUsWUFBWSxDQUFDLENBQUMsUUFBUSxDQUFDLENBQUM7aUJBQ3ZDO2FBQ0Y7UUFDSCxDQUFDO0tBQ0YsQ0FBQztBQUNKLENBQUM7QUFFRCxlQUFlLFdBQVcsQ0FBQyJ9